import { useCallback, useMemo } from 'react';
import {
  MAP_ENTITY_PARAM_VALUES,
  predefinedTemplates,
} from 'constants/entities';
import {
  CREATE_QUICK_ENTITY_MODAL,
  ENTITY_DETAILS_MODAL,
} from 'constants/modals';
import { Geometry } from 'geojson';
import { createMapObject } from 'routes/Map/utils';
import { commonToolSelector } from 'store/slices/mapV2/mapReducer/toolsReducer/commonSlice/selectors';
import { predefinedTemplateSelector } from 'store/slices/mapV2/tabsReducer/layersReducer/mapEntitiesSlice/selectors';
import { stateActions as layersStateActions } from 'store/slices/mapV2/tabsReducer/layersReducer/stateSlice';
import { temporaryEntitiesSelector } from 'store/slices/mapV2/tabsReducer/layersReducer/stateSlice/selectors';
import { modalsActions } from 'store/slices/service/modalsSlice';
import { modalSelector } from 'store/slices/service/modalsSlice/selectors';
import { MapCommonModes, MapEntity } from 'types';

import { safe } from 'utils';
import { getEntityParametersTitleToIdMap } from 'utils/entity';
import { useDefaultProjectSettingsLocalStorage } from 'utils/localStorage';

import { useAppDispatch, useAppSelector } from './redux';
import { useMapDraw } from './useMapDraw';

export const useQuickEntityCreation = () => {
  const dispatch = useAppDispatch();

  const temporaryEntities = useAppSelector(temporaryEntitiesSelector);
  const commonTool = useAppSelector(commonToolSelector);
  const createQuickEntityModal = useAppSelector((state) =>
    modalSelector(state, CREATE_QUICK_ENTITY_MODAL)
  );
  const mapObjectTemplate = useAppSelector((state) =>
    predefinedTemplateSelector(state, predefinedTemplates.MAP_OBJECT)
  );
  const mapLayerTemplate = useAppSelector((state) =>
    predefinedTemplateSelector(state, predefinedTemplates.MAP_LAYER)
  );

  const { handleDrawClear } = useMapDraw();
  const [defaultProjectSettings] = useDefaultProjectSettingsLocalStorage();

  const isCreateQuickEntityModalOpen =
    createQuickEntityModal?.isOpen &&
    commonTool?.type === MapCommonModes.create_quick_entity;

  const paramIdMap = useMemo(() => {
    if (!mapObjectTemplate?.parameters) return {};

    return getEntityParametersTitleToIdMap(
      mapObjectTemplate.parameters,
      MAP_ENTITY_PARAM_VALUES
    );
  }, [mapObjectTemplate]);

  const openModalWithEntity = useCallback(
    (mapEntity: MapEntity, modalId: string) => {
      dispatch(
        modalsActions.addModal({
          id: modalId,
          isOpen: true,
          props: mapEntity,
        })
      );
    },
    [dispatch]
  );

  const handleEntityCreate = useCallback(
    (geometry: Geometry) => {
      if (!mapObjectTemplate || !mapLayerTemplate) return;

      const parentEntityId = Number(defaultProjectSettings?.projectId) || 0;
      const mapObject = createMapObject(
        mapObjectTemplate,
        mapLayerTemplate,
        paramIdMap,
        parentEntityId,
        geometry
      );

      dispatch(
        layersStateActions.setTemporaryEntities([
          ...temporaryEntities,
          mapObject,
        ])
      );

      safe(() => handleDrawClear());
      openModalWithEntity(mapObject, ENTITY_DETAILS_MODAL);
    },
    [
      mapObjectTemplate,
      mapLayerTemplate,
      paramIdMap,
      defaultProjectSettings,
      temporaryEntities,
      handleDrawClear,
      dispatch,
      openModalWithEntity,
    ]
  );

  return {
    createQuickEntityModal,
    commonTool,
    temporaryEntities,
    paramIdMap,
    isCreateQuickEntityModalOpen,
    handleEntityCreate,
    openModalWithEntity,
  };
};
